import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ $isVisible: boolean; $type: 'success' | 'error' | 'info' }>`
  position: relative;
  background-color: #0E1F1F;
  border-radius: 12px;
  padding: 16px 20px;
  min-width: 320px;
  max-width: 480px;
  border-left: 4px solid ${props => {
    switch (props.$type) {
      case 'success': return '#24BA7D';
      case 'error': return '#FF6B35';
      default: return '#609F94';
    }
  }};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  animation: ${props => props.$isVisible ? slideIn : slideOut} 0.3s ease-out forwards;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconContainer = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.$type) {
      case 'success': return '#24BA7D';
      case 'error': return '#FF6B35';
      default: return '#609F94';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Icon = styled.div`
  color: white;
  font-weight: bold;
  font-size: 16px;
`;

const MessageContainer = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
  margin-bottom: 2px;
`;

const Message = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #838D90;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #838D90;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #FFFFFF;
  }
`;

export interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'info';
  title: string;
  message?: string;
  duration?: number;
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ id, type, title, message, duration = 4000, onRemove }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onRemove(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success': return '✓';
      case 'error': return '✕';
      default: return 'i';
    }
  };

  return (
    <ToastContainer $isVisible={isVisible} $type={type}>
      <IconContainer $type={type}>
        <Icon>{getIcon()}</Icon>
      </IconContainer>
      
      <MessageContainer>
        <Title>{title}</Title>
        {message && <Message>{message}</Message>}
      </MessageContainer>
      
      <CloseButton onClick={handleClose}>×</CloseButton>
    </ToastContainer>
  );
};

export default Toast; 